import { useState } from 'react'
import { useDates, useLists, usePages, useProjects, useTasks, useAllEdges } from '@/db/hooks'
import { withSteps } from '@/lib/steps'
import { isOpen, isTemplate } from '@/kinds/task/schema'
import type { TaskResource } from '@/kinds/task/apply'
import { addDays, fmtDateShort, todayLocal } from '@/lib/time'
import { TaskRow } from '@/components/TaskRow'
import { DateRow } from '@/components/DateBits'
import { occurrencesBetween } from '@/lib/dates'
import { dateHue, tokens } from '@/lib/colour'
import type { DateResource } from '@/kinds/date/apply'
import { Button, Empty, PageTitle, cx } from '@/components/ui'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { AppTabs, AHEAD_TABS } from '@/components/AppTabs'

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const monthOf = (date: string) => date.slice(0, 7)
const firstOfMonth = (ym: string) => `${ym}-01`
const shiftMonth = (ym: string, n: number) => {
  const [y, m] = ym.split('-').map(Number) as [number, number]
  const d = new Date(y, m - 1 + n, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}
const monthName = (ym: string) => {
  const [y, m] = ym.split('-').map(Number) as [number, number]
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, {
    month: 'long',
    ...(y === new Date().getFullYear() ? {} : { year: 'numeric' }),
  })
}
/** Monday-first grid covering the month: 5 or 6 rows of 7 dates. */
const gridFor = (ym: string): string[] => {
  const first = firstOfMonth(ym)
  const [y, m] = ym.split('-').map(Number) as [number, number]
  const dow = (new Date(y, m - 1, 1).getDay() + 6) % 7 // 0 = Monday
  const start = addDays(first, -dow)
  const days: string[] = []
  for (let i = 0; i < 42; i++) days.push(addDays(start, i))
  // drop a trailing week that is entirely next month
  while (days.length > 35 && monthOf(days[days.length - 7]!) !== ym) days.splice(days.length - 7, 7)
  return days
}

/**
 * Month view of scheduled tasks (dots, never counts) with the selected day's
 * list underneath. Due dates show as a ring so a deadline reads differently
 * from a plan.
 */
export const CalendarView = () => {
  const today = todayLocal()
  const [month, setMonth] = useState(monthOf(today))
  const [selected, setSelected] = useState(today)
  // reminders on the grid: a bell dot on the day a task/project/page will nudge you (remembered per device)
  const [showReminders, setShowReminders] = useState(() => {
    try {
      return localStorage.getItem('cal_reminders') !== '0'
    } catch {
      return true
    }
  })
  const toggleReminders = () => {
    setShowReminders((v) => {
      try {
        localStorage.setItem('cal_reminders', v ? '0' : '1')
      } catch {
        /* private mode */
      }
      return !v
    })
  }
  const projects = useProjects() ?? []
  const pagesAll = usePages() ?? []
  const listsAll = useLists() ?? []
  const tasks = (useTasks() ?? []).filter((t) => !t.deleted_at && !isTemplate(t.spec))
  const byDay = new Map<string, TaskResource[]>()
  const dueByDay = new Map<string, TaskResource[]>()
  for (const t of tasks) {
    if (!isOpen(t.status.state) && t.spec.scheduled !== selected) continue
    if (t.spec.scheduled) byDay.set(t.spec.scheduled, [...(byDay.get(t.spec.scheduled) ?? []), t])
    if (t.spec.due && t.spec.due !== t.spec.scheduled)
      dueByDay.set(t.spec.due, [...(dueByDay.get(t.spec.due) ?? []), t])
  }
  const localDay = (iso: string) => {
    const d = new Date(iso)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }
  const remindByDay = new Map<
    string,
    {
      title: string
      to: '/tasks/$id' | '/projects/$id' | '/pages/$id' | '/lists/$id'
      id: string
      at: string
    }[]
  >()
  if (showReminders) {
    const add = (
      title: string,
      to: '/tasks/$id' | '/projects/$id' | '/pages/$id' | '/lists/$id',
      id: string,
      at?: string,
    ) => {
      if (!at) return
      const day = localDay(at)
      remindByDay.set(day, [...(remindByDay.get(day) ?? []), { title, to, id, at }])
    }
    for (const t of tasks) add(t.spec.title, '/tasks/$id', t.id, t.spec.remind_at)
    for (const p of projects) if (!p.deleted_at) add(p.spec.title, '/projects/$id', p.id, p.spec.remind_at)
    for (const p of pagesAll) if (!p.deleted_at) add(p.name, '/pages/$id', p.id, p.spec.remind_at)
    for (const l of listsAll)
      if (!l.deleted_at) {
        add(l.spec.title, '/lists/$id', l.id, l.spec.remind_at)
        for (const it of l.spec.items)
          if (!it.done) add(`${it.text} · ${l.spec.title}`, '/lists/$id', l.id, it.remind_at)
      }
  }
  const days = gridFor(month)
  // dates: a dot in the category's hue on each day one falls
  const datesOn = new Map<string, DateResource[]>()
  for (const d of useDates())
    for (const on of occurrencesBetween(d.spec, days[0]!, days[days.length - 1]!))
      datesOn.set(on, [...(datesOn.get(on) ?? []), d])
  const edges = useAllEdges()
  const dayTasks = [
    ...(byDay.get(selected) ?? []),
    ...(dueByDay.get(selected) ?? []).filter((t) => t.spec.scheduled !== selected),
  ].sort((a, b) => ((a.spec.scheduled_time ?? '99') < (b.spec.scheduled_time ?? '99') ? -1 : 1))
  const goToday = () => {
    setMonth(monthOf(today))
    setSelected(today)
  }
  return (
    <div data-testid="calendar">
      <PageTitle
        right={
          <div className="flex items-center gap-1">
            <label className="max-md:hidden flex items-center gap-1.5 text-xs text-muted mr-1 select-none">
              <input
                type="checkbox"
                checked={showReminders}
                onChange={toggleReminders}
                data-testid="calendar-reminders"
              />
              Reminders
            </label>
            <Button variant="chip" onClick={goToday} data-testid="calendar-today">
              Today
            </Button>
            <Button
              variant="chip"
              className="px-2"
              onClick={() => setMonth((m) => shiftMonth(m, -1))}
              aria-label="Previous month"
              data-testid="calendar-prev"
            >
              <ChevronLeft size={16} />
            </Button>
            <Button
              variant="chip"
              className="px-2"
              onClick={() => setMonth((m) => shiftMonth(m, 1))}
              aria-label="Next month"
              data-testid="calendar-next"
            >
              <ChevronRight size={16} />
            </Button>
          </div>
        }
      >
        {/* the month is the title; the year only when it is not this one */}
        {monthName(month)}
      </PageTitle>
      <AppTabs tabs={AHEAD_TABS} label="Calendar" />
      <label className="md:hidden flex items-center justify-end gap-1.5 text-xs text-muted -mt-2 mb-2 select-none">
        <input type="checkbox" checked={showReminders} onChange={toggleReminders} />
        Reminders
      </label>
      <div className="grid grid-cols-7 gap-1 text-center text-[13px] font-semibold text-faint mb-1">
        {WEEKDAYS.map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1" role="grid" aria-label="Month">
        {days.map((d) => {
          const inMonth = monthOf(d) === month
          const dots = (byDay.get(d) ?? []).slice(0, 3)
          const due = dueByDay.get(d)?.length ?? 0
          const isSel = d === selected
          const isToday = d === today
          return (
            <button
              key={d}
              type="button"
              onClick={() => setSelected(d)}
              className={cx(
                'aspect-square min-h-11 rounded-lg flex flex-col items-center justify-start pt-1.5 gap-1 text-sm transition-colors',
                inMonth ? 'text-fg' : 'text-faint',
                isSel ? 'bg-accent-soft ring-1 ring-accent' : 'hover:bg-raised',
              )}
              aria-pressed={isSel}
              aria-label={fmtDateShort(d, today)}
              data-day={d}
            >
              <span
                className={cx(
                  'h-6 w-6 rounded-full flex items-center justify-center',
                  isToday && 'bg-accent text-on-accent font-semibold',
                )}
              >
                {Number(d.slice(8))}
              </span>
              <span className="flex items-center gap-0.5 h-2">
                {dots.map((t) => (
                  <span
                    key={t.id}
                    className={cx(
                      'h-1.5 w-1.5 rounded-full',
                      isOpen(t.status.state) ? 'bg-accent' : 'bg-faint',
                    )}
                  />
                ))}
                {due > 0 && <span className="h-1.5 w-1.5 rounded-full ring-1 ring-amber-400" />}
                {(datesOn.get(d) ?? []).slice(0, 2).map((x) => (
                  <span
                    key={x.id}
                    className="h-1.5 w-1.5 rotate-45 rounded-[1px]"
                    style={{ background: tokens(dateHue(x.spec.category)).stripe }}
                    title={x.spec.title}
                  />
                ))}
                {(remindByDay.get(d)?.length ?? 0) > 0 && (
                  <span className="text-[9px] leading-none text-accent" title="Reminder">
                    🔔
                  </span>
                )}
              </span>
            </button>
          )
        })}
      </div>
      <section className="mt-5" data-testid="calendar-day">
        <h2 className="text-[13px] font-semibold text-muted mb-2">{fmtDateShort(selected, today)}</h2>
        {showReminders && (remindByDay.get(selected)?.length ?? 0) > 0 && (
          <ul className="flex flex-col gap-1 mb-3" data-testid="calendar-reminders-list">
            {remindByDay.get(selected)!.map((r) => (
              <li key={r.id + r.at}>
                <Link
                  to={r.to}
                  params={{ id: r.id }}
                  className="flex items-center gap-2 text-sm rounded-lg bg-panel/60 hover:bg-panel px-3 py-2"
                >
                  <span className="text-accent">🔔</span>
                  <span className="flex-1 truncate">{r.title}</span>
                  <span className="text-xs text-faint">
                    {new Date(r.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
        {(datesOn.get(selected)?.length ?? 0) > 0 && (
          <ul className="group-list mb-3" data-testid="calendar-dates">
            {datesOn.get(selected)!.map((x) => (
              <DateRow key={x.id} date={x} on={selected} today={today} />
            ))}
          </ul>
        )}
        {dayTasks.length === 0 ? (
          datesOn.has(selected) ? null : (
            <Empty icon="·">
              Nothing planned. Schedule something with &gt;{fmtDateShort(selected, today).toLowerCase()} in
              quick-add.
            </Empty>
          )
        ) : (
          <ul className="group-list">
            {withSteps(dayTasks, edges).map(({ task: t, child }, i) => (
              <TaskRow key={t.id} task={t} index={i} mode="compact" child={child} />
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
